import { useT } from '../../i18n/index.ts';
import { Chip, Panel } from '../../ui/index.ts';
import type { AnnouncementSendResult } from './announcementSendResult.ts';

/**
 * What the send just did, stated so it cannot be mistaken for something else
 * (#1946 counts, #1965 audience).
 *
 * THE PANEL'S JOB IS THE COVERAGE SENTENCE. #1945 was not a missing number — the
 * send reported success and an operator read it as fleet-wide. So a whitelist
 * send does not merely render a smaller `orgs_targeted`: it renders a DIFFERENT
 * panel, with its own caption, its own testid and a warn-toned line saying in
 * words that the fleet has not been published to yet. A number an operator has
 * to interpret is a number an operator can misinterpret.
 *
 * FOUR OUTCOMES, NEVER COLLAPSED — asked-for × reported, both directions:
 *   - fleet publish the server confirmed → the neutral result panel;
 *   - whitelist send the server CONFIRMED → the test panel, warn-toned, saying
 *     the fleet has not received it;
 *   - whitelist requested and the server says it went to the FLEET → an alert.
 *     That is a platform that ignored `test_recipients` (a build predating #1965),
 *     and it means an unrecallable fleet-wide write just happened while the
 *     operator was drafting. Rendering it as a successful test send would be
 *     #1945 exactly: the surface asserting a reach it does not have.
 *   - fleet requested and the server says WHITELIST → also an alert, and this
 *     one is #1945's own shape rather than its mirror: the operator believes
 *     every customer has been told and only a handful of orgs were written.
 *     There is no plausible cause on the shipped build, which is the point —
 *     the surface may not fall back to "here is a coverage number, you work it
 *     out", because working it out from a number is what nobody did in #1945.
 */
export function AnnouncementResultPanel({
  result,
}: {
  readonly result: AnnouncementSendResult;
}): JSX.Element {
  const t = useT();
  const askedToNarrow = result.requestedMode === 'whitelist';
  const narrowed = askedToNarrow && result.mode === 'whitelist';
  // Requested a whitelist, was told the fleet. Not a mode the console can fix,
  // and not one it may soften. Written as `!== 'whitelist'` rather than
  // `=== 'fleet'` on purpose: the platform this check exists for — one that
  // predates #1965 — reports NO mode at all, so an "equivalent" positive test
  // would pass the very case being detected straight through.
  const widened = askedToNarrow && result.mode !== 'whitelist';
  // The other direction. A fleet publish reported as a whitelist means the
  // operator thinks every customer was told and a handful of orgs were written
  // — #1945 itself. Absent mode is NOT this case (that is an old platform doing
  // exactly what a fleet publish asks for), so this one tests positively.
  const narrowedUnasked = !askedToNarrow && result.mode === 'whitelist';
  // Fewer orgs written than named. The route refuses unresolved ids before
  // writing, so this needs an org to have disappeared between the check and the
  // write — rare, and still not something to leave the operator to spot by
  // comparing two numbers in different places.
  const shortfall = narrowed && result.orgsTargeted !== result.requestedOrgCount;

  return (
    <Panel
      caption={narrowed ? t('announcements.result.test.caption') : t('announcements.result.caption')}
      padded
      testId={narrowed ? 'announcements-result-test' : 'announcements-result'}
    >
      {widened ? (
        <p
          className="m-0 mb-3 text-dense text-bad"
          role="alert"
          data-testid="announcements-result-mode-mismatch"
        >
          <Chip tone="bad">{t('announcements.result.mode_mismatch.tag')}</Chip>{' '}
          {t('announcements.result.mode_mismatch')}
        </p>
      ) : null}
      {narrowedUnasked ? (
        <p
          className="m-0 mb-3 text-dense text-bad"
          role="alert"
          data-testid="announcements-result-mode-mismatch-narrowed"
        >
          <Chip tone="bad">{t('announcements.result.mode_mismatch.tag')}</Chip>{' '}
          {t('announcements.result.mode_mismatch.narrowed', {
            count: String(result.orgsTargeted),
          })}
        </p>
      ) : null}
      {narrowed ? (
        // Said BEFORE the numbers, in prose, and in the warn tone: the operator
        // reading this panel has to leave it knowing the fleet is still waiting.
        <p
          className="m-0 mb-3 text-dense text-warn"
          role="status"
          data-testid="announcements-result-test-scope"
        >
          <Chip tone="warn">{t('announcements.result.test.scope.tag')}</Chip>{' '}
          {t('announcements.result.test.scope', { count: String(result.orgsTargeted) })}
        </p>
      ) : null}
      <dl className="grid gap-3 sm:grid-cols-4">
        <ResultCell label={t('announcements.result.targeted')} value={String(result.orgsTargeted)} />
        <ResultCell label={t('announcements.result.created')} value={String(result.created)} />
        <ResultCell label={t('announcements.result.resurfaced')} value={String(result.resurfaced)} />
        <div className="flex flex-col gap-1">
          <dt className="text-label text-paper-mid">{t('announcements.result.fingerprint')}</dt>
          <dd className="m-0 break-all font-mono text-micro text-paper">{result.fingerprint}</dd>
        </div>
      </dl>
      {shortfall ? (
        <p className="mt-3 mb-0 text-dense text-bad" role="alert">
          {t('announcements.result.test.shortfall', {
            requested: String(result.requestedOrgCount),
            targeted: String(result.orgsTargeted),
          })}
        </p>
      ) : null}
      {result.resurfaced > 0 && narrowed ? (
        // A REWRITE ON THE TEST ARM HAS ONLY THE BAD CAUSE. The routine
        // explanation for an overwrite — "you test sent this slug and are now
        // publishing it" — cannot apply here: this IS the test send, so an
        // existing row under this fingerprint belongs to some other
        // announcement, and the named orgs have just had its text replaced by
        // an unproofed draft with no way back. The fleet panel's two-cause copy
        // would dilute that into something dismissible, which is why this arm
        // gets its own sentence and an alert rather than a status.
        <p
          className="mt-3 mb-0 text-dense text-bad"
          role="alert"
          data-testid="announcements-result-test-recycled"
        >
          <Chip tone="bad">{t('announcements.result.test.recycled.tag')}</Chip>{' '}
          {t('announcements.result.test.recycled', { count: String(result.resurfaced) })}
        </p>
      ) : result.resurfaced > 0 ? (
        // The outcome an operator can get wrong without noticing — but no
        // longer attributable to ONE cause. Since #1965 a rewrite is the normal
        // ending of a drafting flow (test send, then publish), and copy that
        // called every rewrite a recycled slug would train operators to dismiss
        // the line that also reports a genuinely destroyed announcement. It
        // names both causes and keeps the consequence of the second one intact.
        <p className="mt-3 mb-0 text-dense text-warn" role="status">
          <Chip tone="warn">{t('announcements.result.recycled.tag')}</Chip>{' '}
          {t('announcements.result.recycled')}
        </p>
      ) : null}
    </Panel>
  );
}

function ResultCell({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-label text-paper-mid">{label}</dt>
      <dd className="m-0 font-mono text-title text-paper">{value}</dd>
    </div>
  );
}
